import React, { useContext, useState } from 'react';
import {
  NativeBaseProvider, ScrollView, View,
} from 'native-base';
import {
  Button, Chip, Dialog, IconButton, List, Portal,
  ProgressBar, TextInput,
} from 'react-native-paper';
import { useMutation, useQuery } from '@apollo/client';
import { useToast } from 'react-native-toast-notifications';
import { differenceInDays, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ModalContext } from '../../context/ModalContext';
import { FindOneEvent } from '../../gql/event/query';
import {
  ConfirmEvent, LeaveEvent, ValidateEventDeposit, RevokeEventDeposit,
  AddPlayerToEventAsAdmin,
} from '../../gql/event/mutation';
import { AllUsersQuery } from '../../gql/user/query';
import { AuthContext } from '../../context/AuthContext';
import { navigate } from '../../utils/navigation';
import { SCREEN_NAMES } from '../../utils/constant';
import { computeCategorie, computeWeightBasedOnCategorie } from '../../utils/utils';

function PlayersOnEvent({ eventId }) {
  const {
    displayPlayersOnEventModal,
    togglePlayersOnEventModal,
  } = useContext(ModalContext);

  const { isSuperAdmin } = useContext(AuthContext);
  const toast = useToast();

  const { data, loading, error } = useQuery(
    FindOneEvent,
    { variables: { id: eventId } },
  );
  const isAdd = displayPlayersOnEventModal.action === 'ADD';

  const [showAddParticipant, setShowAddParticipant] = useState(
    displayPlayersOnEventModal.action === 'ADD_PLAYER',
  );
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const { data: allUsersData } = useQuery(AllUsersQuery, {
    skip: !isSuperAdmin || !showAddParticipant,
  });

  const refetchQueries = [FindOneEvent, 'FindOneEvent'];

  const [confirmPlayerOnEvent] = useMutation(ConfirmEvent, {
    onError: (err) => toast.show(err.message),
  });
  const [deletePlayerOnEvent] = useMutation(LeaveEvent);
  const [validateEventDeposit] = useMutation(ValidateEventDeposit, {
    onError: (err) => toast.show(err.message),
    refetchQueries,
  });
  const [revokeEventDeposit] = useMutation(RevokeEventDeposit, {
    onError: (err) => toast.show(err.message),
    refetchQueries,
  });
  const [addPlayerToEventAsAdmin] = useMutation(AddPlayerToEventAsAdmin, {
    onError: (err) => toast.show(err.message),
    refetchQueries,
  });

  const existingPlayerIds = new Set(
    (data?.findOneEvent?.players ?? []).map((p) => p.player.id),
  );
  const query = addSearchQuery.trim().toLowerCase();
  const addCandidates = (allUsersData?.allUsers ?? [])
    .filter((u) => !existingPlayerIds.has(u.id))
    .filter((u) => !query
      || u.firstName?.toLowerCase().includes(query)
      || u.lastName?.toLowerCase().includes(query)
      || u.email?.toLowerCase().includes(query));

  return (
    <Dialog
      visible={displayPlayersOnEventModal}
      dismissable
      onDismiss={togglePlayersOnEventModal}
    >
      { loading
        ? <ProgressBar progress={1} indeterminate />
        : (
          <>
            <Dialog.Title>
              {showAddParticipant
                ? `${data?.findOneEvent?.name}: ajouter un participant`
                : `${data?.findOneEvent?.name}: ${isAdd ? `liste d\'attente (${data?.findOneEvent?.players?.filter((p) => !p.isConfirmed).length}/${data?.findOneEvent?.players?.length})` : `inscrits (${data?.findOneEvent?.players?.filter((p) => p.isConfirmed).length}/${data?.findOneEvent?.players?.length})`}`}
            </Dialog.Title>
            {isSuperAdmin && (
              <View style={{ paddingHorizontal: 24, paddingBottom: 8 }}>
                <Button
                  mode="outlined"
                  icon={showAddParticipant ? 'arrow-left' : 'account-plus'}
                  compact
                  onPress={() => setShowAddParticipant(!showAddParticipant)}
                >
                  {showAddParticipant ? 'Retour à la liste' : 'Ajouter un participant'}
                </Button>
              </View>
            )}
            {showAddParticipant ? (
              <Dialog.ScrollArea>
                <View style={{ paddingVertical: 8 }}>
                  <TextInput
                    mode="outlined"
                    placeholder="Rechercher un utilisateur (nom, email)"
                    value={addSearchQuery}
                    onChangeText={setAddSearchQuery}
                  />
                </View>
                <ScrollView style={{ maxHeight: 350 }}>
                  {addCandidates.map((u) => (
                    <List.Item
                      key={u.id}
                      title={`${u.firstName} ${u.lastName}`}
                      description={`${u.email} — ${u.status}`}
                      right={() => (
                        <IconButton
                          icon="plus"
                          onPress={() => addPlayerToEventAsAdmin({
                            variables: { eventId, userId: u.id },
                          })}
                        />
                      )}
                    />
                  ))}
                  {addCandidates.length === 0 && (
                    <List.Item title="Aucun utilisateur trouvé" />
                  )}
                </ScrollView>
              </Dialog.ScrollArea>
            ) : (
              <Dialog.ScrollArea>
                <ScrollView style={{ maxHeight: 400 }}>
                  {data?.findOneEvent?.players
                    ?.filter((p) => (isAdd ? !p.isConfirmed : p.isConfirmed)).map((p) => {
                    // Compute category and weight based on the event
                      const category = computeCategorie(
                        new Date(p.player.profile.dateOfBirth),
                        data?.findOneEvent?.season,
                        data?.findOneEvent?.level,
                      );
                      const lastWeight = p.player.profile.weights.length
                        ? p.player.profile.weights[p.player.profile.weights.length - 1]
                        : null;
                      const weightCategory = lastWeight
                        ? computeWeightBasedOnCategorie(
                          lastWeight.value,
                          p.player.profile.gender,
                          category[0],
                        )
                        : '??';
                      const isWeightMissing = !lastWeight;
                      const isWeightStale = !!lastWeight
                      && differenceInDays(new Date(), new Date(lastWeight.createdAt)) > 30;
                      const isWeightInvalid = isWeightMissing || isWeightStale;
                      return (
                        <List.Item
                          key={p.player.id}
                          title={`${p.player.firstName} ${p.player.lastName}`}
                          description={() => (
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                              <Chip
                                icon={isWeightInvalid ? 'alert' : undefined}
                                style={{ marginRight: 5, marginBottom: 5 }}
                              >
                                {weightCategory}
                                {' '}
                                kg
                                {isWeightMissing && ' (aucune pesée enregistrée)'}
                                {isWeightStale
                                && ` (pesée obsolète, ${formatDistanceToNow(new Date(lastWeight.createdAt), { addSuffix: true, locale: fr })})`}
                              </Chip>
                              <Chip style={{ marginRight: 5, marginBottom: 5 }}>{category[0]}</Chip>
                              {isAdd && (
                              <Chip
                                icon={p.depositProvided ? 'check-circle' : 'alert'}
                                style={{ marginBottom: 5 }}
                                disabled={!isSuperAdmin}
                                onPress={isSuperAdmin ? () => {
                                  const variables = { playerId: p.player.id, eventId };
                                  if (p.depositProvided) {
                                    revokeEventDeposit({ variables });
                                  } else {
                                    validateEventDeposit({ variables });
                                  }
                                } : undefined}
                              >
                                {p.depositProvided ? 'Caution fournie' : 'Caution non fournie'}
                              </Chip>
                              )}
                            </View>
                          )}
                          left={() => (
                            <IconButton
                              icon="file-eye"
                              onPress={() => {
                                navigate(
                                  SCREEN_NAMES.PROFILE,
                                  { email: p.player.email, tabIndex: 1 },
                                );
                                togglePlayersOnEventModal();
                              }}
                            />
                          )}
                          right={() => (
                            <View style={{
                              flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end',
                            }}
                            >
                              <IconButton
                                size={20}
                                style={{ margin: 0 }}
                                icon={isAdd ? 'plus' : 'minus'}
                                disabled={isAdd && (!p.depositProvided || isWeightInvalid)}
                                onPress={() => {
                                  confirmPlayerOnEvent(
                                    {
                                      variables: {
                                        playerId: p.player.id, eventId, isConfirmed: isAdd,
                                      },
                                      refetchQueries: [
                                        FindOneEvent,
                                        'FindOneEvent',
                                      ],
                                    },
                                  );
                                }}
                              />
                              {isSuperAdmin && (
                              <IconButton
                                size={20}
                                style={{ margin: 0 }}
                                icon="delete"
                                onPress={() => deletePlayerOnEvent({
                                  variables: { eventId, userId: p.player.id },
                                  refetchQueries: [
                                    FindOneEvent,
                                    'FindOneEvent',
                                  ],
                                })}
                              />
                              )}
                            </View>
                          )}
                        />
                      );
                    })}
                </ScrollView>
              </Dialog.ScrollArea>
            )}
          </>
        )}
      <Dialog.Actions>
        <Button onPress={togglePlayersOnEventModal}>
          Fermer
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}

export default function PlayersOnEventModal({ eventId }) {
  return (
    <Portal>
      <NativeBaseProvider>
        <PlayersOnEvent eventId={eventId} />
      </NativeBaseProvider>
    </Portal>
  );
}
